<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import socialCommentAutomationsAPI from 'dashboard/api/socialCommentAutomations';
import instagramClient from 'dashboard/api/channel/instagramClient';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const isLoading = ref(true);
const channels = ref([]);
const reconnectingId = ref(null);

const prefix =
  'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.CONNECTION_HEALTH';

const statusFor = channel => {
  if (channel.reauthorization_required) {
    return { key: 'STATUS_REAUTH', classes: 'bg-n-ruby-3 text-n-ruby-11' };
  }
  if (channel.comments_ready === true) {
    return { key: 'STATUS_READY', classes: 'bg-n-teal-3 text-n-teal-11' };
  }
  if (channel.comments_ready === false) {
    return {
      key: 'STATUS_NEEDS_RECONNECT',
      classes: 'bg-n-amber-3 text-n-amber-11',
    };
  }
  return { key: 'STATUS_UNKNOWN', classes: 'bg-n-alpha-2 text-n-slate-11' };
};

const isReady = channel =>
  channel.comments_ready === true && !channel.reauthorization_required;

const iconFor = channel =>
  channel.channel_type === INBOX_TYPES.INSTAGRAM
    ? 'i-lucide-instagram'
    : 'i-lucide-facebook';

const loadHealth = async () => {
  isLoading.value = true;
  try {
    const { data } = await socialCommentAutomationsAPI.connectionHealth();
    channels.value = data?.data || [];
  } catch (error) {
    useAlert(t(`${prefix}.LOAD_ERROR`));
    channels.value = [];
  } finally {
    isLoading.value = false;
  }
};

const reconnect = async channel => {
  if (channel.channel_type === INBOX_TYPES.FB) {
    window.location.href = `/app/accounts/${window.location.pathname.split('/')[3]}/settings/inboxes/${channel.inbox_id}`;
    return;
  }
  try {
    reconnectingId.value = channel.inbox_id;
    const {
      data: { url },
    } = await instagramClient.generateAuthorization();
    window.location.href = url;
  } catch (error) {
    useAlert(t(`${prefix}.LOAD_ERROR`));
  } finally {
    reconnectingId.value = null;
  }
};

onMounted(loadHealth);
</script>

<template>
  <div v-if="isLoading || channels.length" class="flex flex-col gap-3">
    <div class="flex flex-col gap-0.5">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t(`${prefix}.TITLE`) }}
      </h3>
      <p class="text-sm text-n-slate-11">{{ t(`${prefix}.DESCRIPTION`) }}</p>
    </div>

    <div v-if="isLoading" class="p-4 text-sm text-n-slate-11">
      {{ t(`${prefix}.STATUS_CHECKING`) }}
    </div>

    <ul v-else class="flex flex-col gap-2">
      <li
        v-for="channel in channels"
        :key="channel.inbox_id"
        class="flex items-center justify-between gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
      >
        <div class="flex items-center gap-2 min-w-0">
          <span
            :class="iconFor(channel)"
            class="size-4 text-n-slate-11 shrink-0"
          />
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ channel.name }}
          </span>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <span
            class="px-2 py-0.5 text-xs font-medium rounded-full"
            :class="statusFor(channel).classes"
          >
            {{ t(`${prefix}.${statusFor(channel).key}`) }}
          </span>
          <Button
            :label="t(`${prefix}.RECONNECT`)"
            :variant="isReady(channel) ? 'ghost' : 'solid'"
            color="slate"
            size="sm"
            :is-loading="reconnectingId === channel.inbox_id"
            @click="reconnect(channel)"
          />
        </div>
      </li>
    </ul>

    <p class="text-xs text-n-slate-10">{{ t(`${prefix}.APP_REVIEW_NOTE`) }}</p>
  </div>
</template>
